import { hasRole, type Role } from '@rp/contracts'
import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Button } from '../design'
import { applyTheme, readThemePreference, storeThemePreference, type ThemePreference } from '../design/theme'
import { SESSION_COPY } from '../session/copy'
import { AppBarMenu } from './AppBarMenu'
import { SHELL_COPY } from './copy'
import { NAV_ICONS, type NavPath } from './icons'

const NEXT_THEME: Record<ThemePreference, ThemePreference> = { system: 'light', light: 'dark', dark: 'system' }

export type NavEntry = { to: NavPath; label: string; visibleTo: Role }

export const NAV_ENTRIES: readonly NavEntry[] = [
  { to: '/', label: SHELL_COPY.queue, visibleTo: 'reviewer' },
  { to: '/corpus', label: SHELL_COPY.corpus, visibleTo: 'approver' },
  { to: '/knowledge-base', label: SHELL_COPY.knowledge, visibleTo: 'admin' },
  { to: '/usage', label: SHELL_COPY.usage, visibleTo: 'admin' },
  { to: '/settings', label: SHELL_COPY.settings, visibleTo: 'reviewer' },
  { to: '/ops', label: SHELL_COPY.operations, visibleTo: 'admin' },
]

export const visibleNavEntries = (role: Role): readonly NavEntry[] =>
  NAV_ENTRIES.filter((entry) => hasRole(role, entry.visibleTo))

export interface NavBarProps {
  role: Role
  tenantName: string
  userName?: string
  queueUnread?: number
  onSwitchTenant?: () => void
  onSignOut: () => void
}

const QUEUE_PATH: NavPath = '/'
const UNREAD_BADGE_CAP = 99

const UnreadBadge = ({ count }: { count: number }) =>
  count > 0 ? (
    <>
      <span className="nav-badge num" aria-hidden="true">
        {SHELL_COPY.queueUnreadBadge(count, UNREAD_BADGE_CAP)}
      </span>
      <span className="visually-hidden">{SHELL_COPY.queueUnread(count)}</span>
    </>
  ) : null

export const NavBar = ({ role, tenantName, userName, queueUnread = 0, onSwitchTenant, onSignOut }: NavBarProps) => {
  const [theme, setTheme] = useState<ThemePreference>(() => readThemePreference(window.localStorage))
  const roleLabel = SESSION_COPY.roleLabels[role]

  const cycleTheme = () => {
    const next = NEXT_THEME[theme]
    setTheme(next)
    storeThemePreference(window.localStorage, next)
    applyTheme(document.documentElement, next)
  }

  return (
    <>
      <header className="appbar">
        <span className="appbar-tenant">{tenantName}</span>
        <span className="appbar-role chip chip-outline tone-neutral">{roleLabel}</span>
        <div className="appbar-actions">
          <AppBarMenu>
            <Button variant="ghost" onClick={cycleTheme}>
              {SHELL_COPY.theme}：{SHELL_COPY.themeNames[theme]}
            </Button>
            {onSwitchTenant === undefined ? null : (
              <Button variant="ghost" onClick={onSwitchTenant}>
                {SHELL_COPY.switchTenant}
              </Button>
            )}
          </AppBarMenu>
          <Button variant="ghost" onClick={onSignOut}>
            {SHELL_COPY.signOut}
          </Button>
        </div>
      </header>
      <nav className="nav" aria-label={SHELL_COPY.mainNav}>
        <div className="nav-tenant">
          <span className="nav-mark num" aria-hidden="true">
            {SHELL_COPY.brandMark}
          </span>
          <span className="nav-tenant-name">{tenantName}</span>
        </div>
        <ul className="nav-links">
          {visibleNavEntries(role).map((entry) => (
            <li key={entry.to} className="nav-item">
              <Link to={entry.to} className="nav-link" activeProps={{ 'aria-current': 'page' }}>
                <span className="nav-icon">{NAV_ICONS[entry.to]}</span>
                <span className="nav-label">{entry.label}</span>
                {entry.to === QUEUE_PATH ? <UnreadBadge count={queueUnread} /> : null}
              </Link>
            </li>
          ))}
        </ul>
        <div className="nav-user">
          <span className="nav-avatar" aria-hidden="true">
            {(userName ?? roleLabel).slice(0, 1)}
          </span>
          <span className="nav-user-text">
            {userName === undefined ? null : <span className="nav-user-name">{userName}</span>}
            <span className="nav-user-role">{roleLabel}</span>
          </span>
        </div>
      </nav>
    </>
  )
}
